import { useRef, useMemo, useCallback, useDebugValue } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim/index.js';
import { OBJECT, SWRConfig as SWRConfig$1, defaultConfig, withArgs, SWRGlobalState, serialize, createCacheHelper, isUndefined, internalMutate, useIsomorphicLayoutEffect, UNDEFINED, subscribeCallback, IS_SERVER, rAF, IS_REACT_LEGACY, mergeObjects, revalidateEvents, isFunction, getTimestamp } from 'swr/_internal';
export { mutate, preload, useSWRConfig } from 'swr/_internal';

function _arrayLikeToArray(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _arrayWithoutHoles(arr) {
    if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _iterableToArray(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _nonIterableSpread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _toConsumableArray(arr) {
    return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
function _unsupportedIterableToArray(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
var __generator = undefined && undefined.__generator || function(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
};
var WITH_DEDUPE = {
    dedupe: true
};
var useSWRHandler = function(_key, fetcher, config) {
    var cache = config.cache, compare = config.compare, suspense = config.suspense, fallbackData = config.fallbackData, revalidateOnMount = config.revalidateOnMount, refreshInterval = config.refreshInterval, refreshWhenHidden = config.refreshWhenHidden, refreshWhenOffline = config.refreshWhenOffline, keepPreviousData = config.keepPreviousData;
    var ref = SWRGlobalState.get(cache), EVENT_REVALIDATORS = ref[0], MUTATION = ref[1], FETCH = ref[2];
    // `key` is the identifier of the SWR `data` state, `keyInfo` holds extra
    // states such as `error` and `isValidating` inside,
    // all of them are derived from `_key`.
    // `fnArg` is the argument/arguments parsed from the key, which will be passed
    // to the fetcher.
    var ref1 = serialize(_key), key = ref1[0], fnArg = ref1[1];
    // If it's the initial render of this hook.
    var initialMountedRef = useRef(false);
    // If the hook is unmounted already. This will be used to prevent some effects
    // to be called after unmounting.
    var unmountedRef = useRef(false);
    // Refs to keep the key and config.
    var keyRef = useRef(key);
    var fetcherRef = useRef(fetcher);
    var configRef = useRef(config);
    var getConfig = function() {
        return configRef.current;
    };
    var isActive = function() {
        return getConfig().isVisible() && getConfig().isOnline();
    };
    var ref2 = createCacheHelper(cache, key), getCache = ref2[0], setCache = ref2[1], subscribeCache = ref2[2];
    var stateDependencies = useRef({}).current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var fallback = isUndefined(fallbackData) ? config.fallback[key] : fallbackData;
    var isEqual = function(prev, current) {
        var equal = true;
        for(var _ in stateDependencies){
            var t = _;
            if (!compare(current[t], prev[t])) {
                if (t === "data" && isUndefined(prev[t])) {
                    if (!compare(current[t], fallback)) {
                        equal = false;
                    }
                } else {
                    equal = false;
                }
            }
        }
        return equal;
    };
    var getSnapshot = useMemo(function() {
        var shouldStartRequest = function() {
            if (!key) return false;
            if (!fetcher) return false;
            // If `revalidateOnMount` is set, we take the value directly.
            if (!isUndefined(revalidateOnMount)) return revalidateOnMount;
            // If it's paused, we skip revalidation.
            if (getConfig().isPaused()) return false;
            if (suspense) return false;
            return true;
        }();
        var getSelectedCache = function() {
            var state = getCache();
            // We only select the needed fields from the state.
            var snapshot = mergeObjects(state);
            delete snapshot._k;
            if (!shouldStartRequest) {
                return snapshot;
            }
            return Object.assign({
                isValidating: true,
                isLoading: true
            }, snapshot);
        };
        var memorizedSnapshot = getSelectedCache();
        return function() {
            var snapshot = getSelectedCache();
            return isEqual(snapshot, memorizedSnapshot) ? memorizedSnapshot : memorizedSnapshot = snapshot;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        cache,
        key
    ]);
    // Get the current state that SWR should return.
    var cached = useSyncExternalStore(useCallback(function(callback) {
        return subscribeCache(key, function(prev, current) {
            if (!isEqual(prev, current)) callback();
        });
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        cache,
        key
    ]), getSnapshot, getSnapshot);
    var isInitialMount = !initialMountedRef.current;
    var cachedData = cached.data;
    var data = isUndefined(cachedData) ? fallback : cachedData;
    var error = cached.error;
    // Use a ref to store previously returned data. Use the initial data as its initial value.
    var laggyDataRef = useRef(data);
    var returnedData = keepPreviousData ? isUndefined(cachedData) ? laggyDataRef.current : cachedData : data;
    // - Suspense mode and there's stale data for the initial render.
    // - Not suspense mode and there is no fallback data and `revalidateIfStale` is enabled.
    // - `revalidateIfStale` is enabled but `data` is not defined.
    var shouldDoInitialRevalidation = function() {
        // If `revalidateOnMount` is set, we take the value directly.
        if (isInitialMount && !isUndefined(revalidateOnMount)) return revalidateOnMount;
        // If it's paused, we skip revalidation.
        if (getConfig().isPaused()) return false;
        // Under suspense mode, it will always fetch on render if there is no
        // stale data so no need to revalidate immediately mount it again.
        // If data exists, only revalidate if `revalidateIfStale` is true.
        if (suspense) return isUndefined(data) ? false : config.revalidateIfStale;
        // If there is no stale data, we need to revalidate when mount;
        // If `revalidateIfStale` is set to true, we will always revalidate.
        return isUndefined(data) || config.revalidateIfStale;
    }();
    // Resolve the default validating state:
    // If it's able to validate, and it should revalidate when mount, this will be true.
    var defaultValidatingState = !!(key && fetcher && isInitialMount && shouldDoInitialRevalidation);
    var isValidating = isUndefined(cached.isValidating) ? defaultValidatingState : cached.isValidating;
    var isLoading = isUndefined(cached.isLoading) ? defaultValidatingState : cached.isLoading;
    // The revalidation function is a carefully crafted wrapper of the original
    // `fetcher`, to correctly handle the many edge cases.
    var revalidate = useCallback(/*#__PURE__*/ _asyncToGenerator(function(revalidateOpts) {
        var currentFetcher, newData, startAt, loading, opts, _tmp, shouldStartNewRequest, callbackSafeguard, finalState, _tmp1, finishRequestAndUpdateState, cleanupState, initialState, _tmp2, ref, mutationInfo, cacheData, err, currentConfig, shouldRetryOnError, _tmp3;
        return __generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    currentFetcher = fetcherRef.current;
                    if (!key || !currentFetcher || unmountedRef.current || getConfig().isPaused()) {
                        return [
                            2,
                            false
                        ];
                    }
                    loading = true;
                    _tmp = {};
                    opts = revalidateOpts || _tmp;
                    shouldStartNewRequest = !FETCH[key] || !opts.dedupe;
                    callbackSafeguard = function() {
                        if (IS_REACT_LEGACY) {
                            return !unmountedRef.current && key === keyRef.current && initialMountedRef.current;
                        }
                        return key === keyRef.current;
                    };
                    _tmp1 = {};
                    finalState = (_tmp1.isValidating = false, _tmp1.isLoading = false, _tmp1);
                    finishRequestAndUpdateState = function() {
                        setCache(finalState);
                    };
                    cleanupState = function() {
                        // Check if it's still the same request before deleting it.
                        var requestInfo = FETCH[key];
                        if (requestInfo && requestInfo[1] === startAt) {
                            delete FETCH[key];
                        }
                    };
                    _tmp2 = {};
                    initialState = (_tmp2.isValidating = true, _tmp2);
                    // It is in the `isLoading` state, if and only if there is no cached data.
                    // This bypasses fallback data and laggy data.
                    if (isUndefined(getCache().data)) {
                        initialState.isLoading = true;
                    }
                    _state.label = 1;
                case 1:
                    _state.trys.push([
                        1,
                        3,
                        ,
                        4
                    ]);
                    if (shouldStartNewRequest) {
                        setCache(initialState);
                        // If no cache is being rendered currently (it shows a blank page),
                        // we trigger the loading slow event.
                        if (config.loadingTimeout && isUndefined(getCache().data)) {
                            setTimeout(function() {
                                if (loading && callbackSafeguard()) {
                                    getConfig().onLoadingSlow(key, config);
                                }
                            }, config.loadingTimeout);
                        }
                        // Start the request and save the timestamp.
                        // Key must be truthy if entering here.
                        FETCH[key] = [
                            currentFetcher(fnArg),
                            getTimestamp()
                        ];
                    }
                    ref = FETCH[key], newData = ref[0], startAt = ref[1];
                    return [
                        4,
                        newData
                    ];
                case 2:
                    newData = _state.sent();
                    if (shouldStartNewRequest) {
                        // If the request isn't interrupted, clean it up after the
                        // deduplication interval.
                        setTimeout(cleanupState, config.dedupingInterval);
                    }
                    // If there're other ongoing request(s), started after the current one,
                    // we need to ignore the current one to avoid possible race conditions:
                    //   req1------------------>res1        (current one)
                    //        req2---------------->res2
                    // the request that fired later will always be kept.
                    // The timestamp maybe be `undefined` or a number
                    if (!FETCH[key] || FETCH[key][1] !== startAt) {
                        if (shouldStartNewRequest) {
                            if (callbackSafeguard()) {
                                getConfig().onDiscarded(key);
                            }
                        }
                        return [
                            2,
                            false
                        ];
                    }
                    // Clear error.
                    finalState.error = UNDEFINED;
                    mutationInfo = MUTATION[key];
                    if (!isUndefined(mutationInfo) && // case 1
                    (startAt <= mutationInfo[0] || // case 2
                    startAt <= mutationInfo[1] || // case 3
                    mutationInfo[1] === 0)) {
                        finishRequestAndUpdateState();
                        if (shouldStartNewRequest) {
                            if (callbackSafeguard()) {
                                getConfig().onDiscarded(key);
                            }
                        }
                        return [
                            2,
                            false
                        ];
                    }
                    cacheData = getCache().data;
                    // Since the compare fn could be custom fn
                    // cacheData might be different from newData even when compare fn returns True
                    finalState.data = compare(cacheData, newData) ? cacheData : newData;
                    // Trigger the successful callback if it's the original request.
                    if (shouldStartNewRequest) {
                        if (callbackSafeguard()) {
                            getConfig().onSuccess(newData, key, config);
                        }
                    }
                    return [
                        3,
                        4
                    ];
                case 3:
                    err = _state.sent();
                    cleanupState();
                    currentConfig = getConfig();
                    shouldRetryOnError = currentConfig.shouldRetryOnError;
                    _tmp3 = {};
                    // Not paused, we continue handling the error. Otherwise, discard it.
                    if (!currentConfig.isPaused()) {
                        // Get a new error, don't use deep comparison for errors.
                        finalState.error = err;
                        // Error event and retry logic. Only for the actual request, not
                        // deduped ones.
                        if (shouldStartNewRequest && callbackSafeguard()) {
                            currentConfig.onError(err, key, currentConfig);
                            if (shouldRetryOnError === true || isFunction(shouldRetryOnError) && shouldRetryOnError(err)) {
                                if (isActive()) {
                                    // If it's inactive, stop. It will auto-revalidate when
                                    // refocusing or reconnecting.
                                    // When retrying, deduplication is always enabled.
                                    currentConfig.onErrorRetry(err, key, currentConfig, revalidate, (_tmp3.retryCount = (opts.retryCount || 0) + 1, _tmp3.dedupe = true, _tmp3));
                                }
                            }
                        }
                    }
                    return [
                        3,
                        4
                    ];
                case 4:
                    // Mark loading as stopped.
                    loading = false;
                    // Update the current hook's state.
                    finishRequestAndUpdateState();
                    return [
                        2,
                        true
                    ];
            }
        });
    }), // `setState` is immutable, and `eventsCallback`, `fnArg`, and
    // `keyValidating` are depending on `key`, so we can exclude them from
    // the deps array.
    //
    // FIXME:
    // `fn` and `config` might be changed during the lifecycle,
    // but they might be changed every render like this.
    // `useSWR('key', () => fetch('/api/'), { suspense: true })`
    // So we omit the values from the deps array
    // even though it might cause unexpected behaviors.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        key,
        cache
    ]);
    // Similar to the global mutate but bound to the current cache and key.
    // `cache` isn't allowed to change during the lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var boundMutate = useCallback(// Use callback to make sure `keyRef.current` returns latest result every time
    function() {
        for(var _len = arguments.length, args = new Array(_len), _$_key = 0; _$_key < _len; _$_key++){
            args[_$_key] = arguments[_$_key];
        }
        return internalMutate.apply(void 0, [
            cache,
            keyRef.current
        ].concat(_toConsumableArray(args)));
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    // The logic for updating refs.
    useIsomorphicLayoutEffect(function() {
        fetcherRef.current = fetcher;
        configRef.current = config;
        // Handle laggy data updates. If there's cached data of the current key,
        // it'll be the correct reference.
        if (!isUndefined(cachedData)) {
            laggyDataRef.current = cachedData;
        }
    });
    // After mounted or key changed.
    useIsomorphicLayoutEffect(function() {
        if (!key) return;
        var softRevalidate = revalidate.bind(UNDEFINED, WITH_DEDUPE);
        // Expose revalidators to global event listeners. So we can trigger
        // revalidation from the outside.
        var nextFocusRevalidatedAt = 0;
        var onRevalidate = function(type) {
            if (type == revalidateEvents.FOCUS_EVENT) {
                var now = Date.now();
                if (getConfig().revalidateOnFocus && now > nextFocusRevalidatedAt && isActive()) {
                    nextFocusRevalidatedAt = now + getConfig().focusThrottleInterval;
                    softRevalidate();
                }
            } else if (type == revalidateEvents.RECONNECT_EVENT) {
                if (getConfig().revalidateOnReconnect && isActive()) {
                    softRevalidate();
                }
            } else if (type == revalidateEvents.MUTATE_EVENT) {
                return revalidate();
            }
            return;
        };
        var unsubEvents = subscribeCallback(key, EVENT_REVALIDATORS, onRevalidate);
        // Mark the component as mounted and update corresponding refs.
        unmountedRef.current = false;
        keyRef.current = key;
        initialMountedRef.current = true;
        // Keep the original key in the cache.
        setCache({
            _k: fnArg
        });
        // Trigger a revalidation.
        if (shouldDoInitialRevalidation) {
            if (isUndefined(data) || IS_SERVER) {
                // Revalidate immediately.
                softRevalidate();
            } else {
                // Delay the revalidate if we have data to return so we won't block
                // rendering.
                rAF(softRevalidate);
            }
        }
        return function() {
            // Mark it as unmounted.
            unmountedRef.current = true;
            unsubEvents();
        };
    }, [
        key
    ]);
    // Polling
    useIsomorphicLayoutEffect(function() {
        var next = function next() {
            // Use the passed interval
            // ...or invoke the function with the updated data to get the interval
            var interval = isFunction(refreshInterval) ? refreshInterval(data) : refreshInterval;
            // We only start the next interval if `refreshInterval` is not 0, and:
            // - `force` is true, which is the start of polling
            // - or `timer` is not 0, which means the effect wasn't canceled
            if (interval && timer !== -1) {
                timer = setTimeout(execute, interval);
            }
        };
        var execute = function execute() {
            // Check if it's OK to execute:
            // Only revalidate when the page is visible, online, and not errored.
            if (!getCache().error && (refreshWhenHidden || getConfig().isVisible()) && (refreshWhenOffline || getConfig().isOnline())) {
                revalidate(WITH_DEDUPE).then(next);
            } else {
                // Schedule the next interval to check again.
                next();
            }
        };
        var timer;
        next();
        return function() {
            if (timer) {
                clearTimeout(timer);
                timer = -1;
            }
        };
    }, [
        refreshInterval,
        refreshWhenHidden,
        refreshWhenOffline,
        key
    ]);
    // Display debug info in React DevTools.
    useDebugValue(returnedData);
    // In Suspense mode, we can't return the empty `data` state.
    // If there is an `error`, the `error` needs to be thrown to the error boundary.
    // If there is no `error`, the `revalidation` promise needs to be thrown to
    // the suspense boundary.
    if (suspense && isUndefined(data) && key) {
        // SWR should throw when trying to use Suspense on the server with React 18,
        // without providing any initial data. See:
        // https://github.com/vercel/swr/issues/1832
        if (!IS_REACT_LEGACY && IS_SERVER) {
            throw new Error("Fallback data is required when using suspense in SSR.");
        }
        // Always update fetcher and config refs even with the Suspense mode.
        fetcherRef.current = fetcher;
        configRef.current = config;
        unmountedRef.current = false;
        throw isUndefined(error) ? revalidate(WITH_DEDUPE) : error;
    }
    return {
        mutate: boundMutate,
        get data () {
            stateDependencies.data = true;
            return returnedData;
        },
        get error () {
            stateDependencies.error = true;
            return error;
        },
        get isValidating () {
            stateDependencies.isValidating = true;
            return isValidating;
        },
        get isLoading () {
            stateDependencies.isLoading = true;
            return isLoading;
        }
    };
};
var SWRConfig = OBJECT.defineProperty(SWRConfig$1, "defaultValue", {
    value: defaultConfig
});
var unstable_serialize = function(key) {
    return serialize(key)[0];
};
/**
 * @link https://swr.vercel.app/
 * @example
 * ```jsx
 * import useSWR from 'swr'
 * function Profile() {
 *   const { data, error } = useSWR('/api/user', fetcher)
 *   if (error) return <div>failed to load</div>
 *   if (!data) return <div>loading...</div>
 *   return <div>hello {data.name}!</div>
 * }
 * ```
 */ var useSWR = withArgs(useSWRHandler);

// useSWR

export { SWRConfig, useSWR as default, unstable_serialize };
